/**
 * TeamAttendance.jsx — Takım Puantaj Sayfası (MANAGER)
 * KPI Dashboard + Filtreler + Tablo + Toplu Onay
 */
import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Toaster, toast } from 'react-hot-toast';
import api from '../../../api/axios';
import AttendanceDashboard from '../components/AttendanceDashboard';
import AttendanceFilterBar from '../components/AttendanceFilterBar';
import AttendanceTable from '../components/AttendanceTable';
import useAttendanceFilters from '../hooks/useAttendanceFilters';
import { computeStats } from '../utils/attendanceHelpers';

const TeamAttendance = () => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === 'ar';

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [totalEmployees, setTotalEmployees] = useState(0);

  const {
    filters,
    draft,
    activeFilterMenu,
    activeFilterCount,
    openFilterMenu,
    applyFilters,
    resetFilters,
    hasActiveFilter,
    setDraftField,
    buildApiParams,
    setActiveFilterMenu,
  } = useAttendanceFilters();

  // Yan verileri çek (departman, mesai planı)
  useEffect(() => {
    const fetchMeta = async () => {
      try {
        const [deptRes, empRes] = await Promise.all([
          api.get('/employee/department/list'),
          api.get('/employee/list?status=ACTIVE'),
        ]);
        setDepartments(deptRes.data || []);
        setTotalEmployees((empRes.data || []).length);
      } catch {
        // silent
      }
    };
    fetchMeta();
  }, []);

  // Filtreler değişince verileri çek
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = buildApiParams();
      const res = await api.get('/attendance/list', { params });
      setRecords(res.data || []);
      setSelectedIds([]);
    } catch {
      toast.error(t('err_fetch_attendance', 'Puantaj verileri çekilemedi.'));
    } finally {
      setLoading(false);
    }
  }, [buildApiParams, t]);

  useEffect(() => {
    fetchData();
  }, [filters, fetchData]);

  // Toplu onay
  const handleBulkApprove = async (ids) => {
    try {
      await api.put('/attendance/approve', { ids, action: 'APPROVE' });
      toast.success(t('attendance_bulk_approved', 'Seçilen kayıtlar onaylandı.'));
      fetchData();
    } catch {
      toast.error(t('err_action_failed', 'İşlem başarısız.'));
    }
  };

  // Export
  const handleExport = async (format) => {
    try {
      const params = buildApiParams();
      params.format = format;
      const res = await api.get('/attendance/export', { params, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `puantaj-${filters.from_date || 'all'}-${filters.to_date || 'all'}.${format === 'csv' ? 'csv' : 'xlsx'}`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success(t('attendance_export_success', 'Dışa aktarım tamamlandı.'));
    } catch {
      toast.error(t('err_export_failed', 'Dışa aktarım başarısız.'));
    }
  };

  const stats = computeStats(records);

  return (
    <div className="space-y-6 animate-in fade-in duration-500" dir={isArabic ? 'rtl' : 'ltr'}>
      <Toaster position={isArabic ? 'top-left' : 'top-right'} />

      {/* KPI Dashboard */}
      <AttendanceDashboard stats={stats} role="MANAGER" totalEmployees={totalEmployees} />

      {/* Filtre Çubuğu */}
      <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
        <AttendanceFilterBar
          filters={filters}
          draft={draft}
          activeFilterMenu={activeFilterMenu}
          hasActiveFilter={hasActiveFilter}
          openFilterMenu={openFilterMenu}
          applyFilters={applyFilters}
          resetFilters={resetFilters}
          setDraftField={setDraftField}
          departments={departments}
          schedules={schedules}
          onExport={handleExport}
          showExport
        />
      </div>

      {/* Veri Tablosu */}
      <AttendanceTable
        records={records}
        loading={loading}
        showEmployee
        showApproval
        showCheckbox
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        onBulkApprove={handleBulkApprove}
      />
    </div>
  );
};

export default TeamAttendance;